import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { VtnViewModel } from '../../../../model/vtns.model';
import {
  convert_sha256,
  DEBOUNCE_TIME,
  extractErrorMessage,
  goBack,
  goToNextPage,
  vtnHealthClass
} from '../../../../common/common';
import {Subject, forkJoin, takeUntil, BehaviorSubject} from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import {NgxPaginationModule} from "ngx-pagination";
import { FormsModule } from "@angular/forms";

@Component({
  selector: 'app-vtn-index',
  standalone: true,
  imports: [RouterModule, CommonModule, LoaderComponent, NgxPaginationModule, FormsModule],
  templateUrl: './vtn-index.component.html',
  styleUrl: './vtn-index.component.scss'
})
export class VtnIndexComponent implements OnInit, OnDestroy {
  protected isLoading: boolean = false;

  // Search fields for VTN parameters
  public idSearch: string = '';
  public vtnIdSearch: string = '';
  public uriSearch: string = '';
  public modeSearch: string = '';
  public caFingerprintSearch: string = '';
  public fingerprintSearch: string = '';

  // Debounce search
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    vtnId: '',
    uri: '',
    mode: '',
  });
  private unsubscribe = new Subject<void>();

  public isAdmin: boolean = false;
  public displayedVtns: VtnViewModel[] = [];
  public router: Router;
  public error: string | null = null;

  protected readonly paginationId = 'vtnsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  constructor(private injector: Injector, private authService: AuthService, private vtnService: ApiService){
    this.router = injector.get(Router);
  }

  ngOnInit() {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadVtns();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.vtnIdSearch) {
      params['vtn_id'] = this.vtnIdSearch.trim();
    }
    if (this.uriSearch) {
      params['uri'] = this.uriSearch.trim();
    }
    if (this.modeSearch) {
      params['mode'] = this.modeSearch.trim();
    }
    // Note: CA fingerprint, push fingerprint searches need client-side filtering
    // since they are computed values or may not be directly searchable

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();
    this.searchParams$.next({
      id: this.idSearch,
      vtnId: this.vtnIdSearch,
      uri: this.uriSearch,
      mode: this.modeSearch,
    });
  }

  loadVtns() {
    this.isLoading = true;
    this.error = null;
    const queryParams = this.buildSearchParams();

    this.vtnService.findVtn(queryParams).pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (response: VtnViewModel[]) => {
        this.displayedVtns = response;
        this.paginationModel = this.pagination.updateTotalItems(response.length);

        // For each VTN, load its status and process certificates/fingerprints
        this.displayedVtns.forEach(vtn => {
          // Set default mode if not present (for backward compatibility)
          if (!vtn.mode) {
            vtn.mode = 'poll';
          }

          // Process CA certificate fingerprint for poll mode
          if (vtn.mode === 'poll') {
            if (vtn.ca){
              const fingerprints = convert_sha256(vtn.ca);
              vtn.ca = fingerprints.primary;
            } else {
              vtn.ca = "No CA uploaded.";
            }
          }

          // Load VTN status only for poll mode (push mode doesn't use polling health checks)
          if (vtn.mode === 'poll') {
            this.vtnService.findVtnStatus(vtn.id).pipe(takeUntil(this.unsubscribe)).subscribe({
              next: (statuses: any) => {
                if (statuses && statuses.length > 0) {
                  // Determine health status based on failures vs limit
                  const isUnhealthy = statuses.some((status: any) => {
                    if (vtn.poll_failure_limit && vtn.poll_failure_limit > 0) {
                      return status.failures >= vtn.poll_failure_limit;
                    } else {
                      return status.failures >= 1; // Default to any failures
                    }
                  });

                  vtn.status = isUnhealthy ? 'unhealthy' : 'healthy';
                } else {
                  vtn.status = 'unknown';
                }
              },
              error: (err) => {
                console.error('Error loading VTN status:', err);
                vtn.status = 'unknown';
                this.error = extractErrorMessage(err, "Failed to load VTN status.");
              }
            });
          } else {
            // Push mode VTNs don't have polling health status
            vtn.status = 'N/A';
          }
        });
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading VTNs:', err);
        this.error = extractErrorMessage(err, "Failed to load VTNs.");
      }
    });
  }

  clear_failures(vtn: any) {
    // Only allow clearing failures for poll mode VTNs
    if (vtn.mode !== 'poll') {
      this.error = 'Health status reset is only available for poll mode VTNs.';
      return;
    }

    const vtn_status = {
      vtn_id: vtn.id
    };

    this.vtnService.putVtnStatus(vtn_status).subscribe({
      next: () => {
        // Update the VTN status to healthy after reset
        vtn.status = 'healthy';
      },
      error: (err) => {
        console.error('Error clearing failures:', err);
        this.error = extractErrorMessage(err, "Failed to reset VTN health status.");
      }
    });
  }

  delete(vtn: any) {
    if (confirm('Are you sure you want to delete this VTN?')) {
      this.vtnService.deleteVtn(vtn.id).subscribe({
        next: () => {
          // Reload the VTN list after deletion
          this.loadVtns();
        },
        error: (err) => {
          console.error('Error deleting VTN:', err);
          this.error = extractErrorMessage(err, "Failed to delete VTN.");
        }
      });
    }
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  vtnClass(vtn: any) {
    return vtnHealthClass(vtn);
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadVtns();
  }
}
